/**
 * Navigation Guard Utility
 *
 * Simple event-based system to manage navigation warnings when there are unsaved changes.
 *
 * @since 1.0.0
 */

import type { NavigationGuardState } from './types';

const state: NavigationGuardState = {
	hasUnsavedChanges: false,
	listeners: new Set(),
};

/**
 * Set the unsaved changes state
 *
 * @since 1.0.0
 *
 * @param hasChanges Whether there are unsaved changes.
 */
export const setUnsavedChanges = (hasChanges: boolean): void => {
	state.hasUnsavedChanges = hasChanges;
	state.listeners.forEach((listener) => listener(hasChanges));
};

/**
 * Get the current unsaved changes state
 *
 * @since 1.0.0
 *
 * @return Whether there are unsaved changes.
 */
export const getUnsavedChanges = (): boolean => {
	return state.hasUnsavedChanges;
};

/**
 * Subscribe to unsaved changes state updates
 *
 * @since 1.0.0
 *
 * @param listener Callback function.
 *
 * @return Unsubscribe function.
 */
export const subscribeToUnsavedChanges = (
	listener: (hasUnsavedChanges: boolean) => void,
): (() => void) => {
	state.listeners.add(listener);

	// Call immediately with current state
	listener(state.hasUnsavedChanges);

	return () => {
		state.listeners.delete(listener);
	};
};

